import React, { useState, useRef } from "react";
import axios from "axios";
import "../styles/Transcription.css";
import audioIcon from "../Assets/audio.webp";
import { Transcribing } from "../pages/Transcribing";
import Result from "../pages/Result";

// Utility: Convert AudioBuffer → WAV Blob
function audioBufferToWavBlob(audioBuffer) {
  const numOfChan = audioBuffer.numberOfChannels;
  const length = audioBuffer.length * numOfChan * 2 + 44;
  const buffer = new ArrayBuffer(length);
  const view = new DataView(buffer);

  // Write WAV header
  const writeString = (view, offset, string) => {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  };

  let offset = 0;
  writeString(view, offset, "RIFF"); offset += 4;
  view.setUint32(offset, 36 + audioBuffer.length * numOfChan * 2, true); offset += 4;
  writeString(view, offset, "WAVE"); offset += 4;
  writeString(view, offset, "fmt "); offset += 4;
  view.setUint32(offset, 16, true); offset += 4;
  view.setUint16(offset, 1, true); offset += 2;
  view.setUint16(offset, numOfChan, true); offset += 2;
  view.setUint32(offset, audioBuffer.sampleRate, true); offset += 4;
  view.setUint32(offset, audioBuffer.sampleRate * numOfChan * 2, true); offset += 4;
  view.setUint16(offset, numOfChan * 2, true); offset += 2;
  view.setUint16(offset, 16, true); offset += 2;
  writeString(view, offset, "data"); offset += 4;
  view.setUint32(offset, audioBuffer.length * numOfChan * 2, true); offset += 4;

  // Write PCM samples
  let interleaved = new Float32Array(audioBuffer.length * numOfChan);
  for (let chan = 0; chan < numOfChan; chan++) {
    interleaved.set(audioBuffer.getChannelData(chan), chan);
  }
  let idx = 44;
  for (let i = 0; i < interleaved.length; i++, idx += 2) {
    let s = Math.max(-1, Math.min(1, interleaved[i]));
    view.setInt16(idx, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }

  return new Blob([view], { type: "audio/wav" });
}


const TranscriptionApp = () => {
  const [audioFile, setAudioFile] = useState(null);
  const [transcription, setTranscription] = useState("");
  const [translatedText, setTranslatedText] = useState("");
  const [targetLang, setTargetLang] = useState("es");
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const [recording, setRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);

const handleFileChange = (e) => {
  const file = e.target.files[0];
  setAudioFile(file);

  // ✅ Save for playback in Result
  const audioUrl = URL.createObjectURL(file);
  localStorage.setItem("tempAudioUrl", audioUrl);
};


  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleUpload = async () => {
  if (!audioFile) return;
  setStatus("transcribing");
  setTranscription("");
  setTranslatedText("");
  setError(null);

  const formData = new FormData();
  formData.append("audio_file", audioFile);

  try {
    const response = await axios.post(
      "http://127.0.0.1:8000/api/upload/",
      formData,
      { headers: { "Content-Type": "multipart/form-data" } }
    );

    const jobId = response.data.id;

    // Poll for transcription result
    const checkStatus = setInterval(async () => {
      try {
        const jobRes = await axios.get(
          `http://127.0.0.1:8000/api/transcription/${jobId}/`
        );

        if (jobRes.data.status === "completed") {
          clearInterval(checkStatus);
          setTranscription(jobRes.data.original_text);
          setTranslatedText(jobRes.data.translated_text);
          setStatus("success");
        } else if (jobRes.data.status === "failed") {
          clearInterval(checkStatus);
          setError(jobRes.data.error_message || "Transcription failed.");
          setStatus("error");
        }
      } catch (err) {
        clearInterval(checkStatus);
        setError("Error checking transcription status.");
        setStatus("error");
      }
    }, 3000); // check every 3 seconds
  } catch (error) {
    console.error("Error uploading:", error);
    setError("Upload failed. Please try again.");
    setStatus("error");
  }
};


  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      const chunks = [];

      mediaRecorder.ondataavailable = (event) => {
        chunks.push(event.data);
      };
mediaRecorder.onstop = async () => {
  const audioBlob = new Blob(chunks, { type: "audio/webm" });

  const arrayBuffer = await audioBlob.arrayBuffer();
  const audioCtx = new AudioContext();
  const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer);

  const wavBlob = audioBufferToWavBlob(audioBuffer);
  const wavFile = new File([wavBlob], "recorded_audio.wav", { type: "audio/wav" });

  setAudioBlob(wavBlob);
  setAudioFile(wavFile);

  // ✅ Save for playback in Result
  const audioUrl = URL.createObjectURL(wavBlob);
  localStorage.setItem("tempAudioUrl", audioUrl);
};



      mediaRecorder.start();
      setRecording(true);
    } catch (err) {
      console.error("Error accessing microphone:", err);
      setError(
        "Microphone access denied. Please enable microphone permissions."
      );
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
  };

  if (status === "transcribing") {
    return <Transcribing />;
  }

  if (status === "success" || status === "error") {
    return (
   <Result
  fileName={audioFile?.name}
  transcription={transcription}
  translation={translatedText}
  targetLang={targetLang}
  setTargetLang={setTargetLang}
  setTranslatedText={setTranslatedText}
  setStatus={setStatus}
  status={status}   // ✅ added
  error={error}
/>

    );
  }

  return (
    <div className="transcription-container">
      <section className="transcript">
        <h1 className="transcription-title">
          <span className="highlight">Transcripto</span> is your personal scribe
        </h1>
        <p className="transcription-description">
          Unlock your full productivity potential with Transcripto, an assistive
          platform that can transcribe any recording with near-human accuracy
          and translate it into your preferred language.
        </p>
        <button className="upload-button" onClick={handleUploadClick}>
          Upload free now
        </button>
      </section>

      <div className="upload-section">
        <h2 className="upload-title">01 Upload or record your audio</h2>
        <input
          type="file"
          accept="audio/*"
          onChange={handleFileChange}
          className="file-input"
          ref={fileInputRef}
        />
        <p className="or-text">or</p>
        <div className="input-container">
          <div className="text-input-wrapper">
            <button
              className={`record-button ${recording ? "recording" : ""}`}
              onClick={recording ? stopRecording : startRecording}
            >
              {recording ? "🔴 Recording..." : "🎙️ Record Audio"}
            </button>
          </div>
        </div>
        <h2 className="transcribe-title">02 Transcribe your recording</h2>
        <button
          onClick={handleUpload}
          className="transcribe-button"
          disabled={!audioFile}
          style={{ cursor: !audioFile ? "not-allowed" : "pointer" }}
        >
          Transcribe
        </button>
      </div>
    </div>
  );
};

export default TranscriptionApp;
